import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    Card,
    CardMedia,
    CardContent,
    Typography,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    CircularProgress
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { fetchCategories, createCategory, updateCategory, deleteCategory, uploadCategoryImage } from '../../services/api';

interface Category {
    _id: string;
    name: string;
    description: string;
    slug: string;
    imageUrl?: string;
    products?: any[];
}

interface FormData {
    name: string;
    description: string;
    slug: string;
    imageUrl?: string;
}

const AdminCategories: React.FC = () => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCategory, setEditingCategory] = useState<Category | null>(null);
    const [formData, setFormData] = useState<FormData>({
        name: '',
        description: '',
        slug: '',
        imageUrl: ''
    });
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const initialFormState: FormData = {
        name: '',
        description: '',
        slug: '',
        imageUrl: ''
    };

    const loadCategories = useCallback(async () => {
        try {
            const data = await fetchCategories();
            setCategories(data);
        } catch (error) {
            console.error('Erreur lors du chargement des catégories:', error);
        }
    }, []);

    useEffect(() => {
        loadCategories();
    }, [loadCategories]);


    const handleOpenDialog = useCallback((category?: Category) => {
        if (category) {
            setEditingCategory(category);
            setFormData({
                ...category,
                imageUrl: category.imageUrl || ''
            });
            // Utiliser directement l'URL de l'image - le proxy gère la redirection
            setImagePreview(category.imageUrl || null);
        } else {
            setEditingCategory(null);
            setFormData(initialFormState);
            setSelectedImage(null);
            setImagePreview(null);
        }
        setOpenDialog(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(false);
        setEditingCategory(null);
        setFormData(initialFormState);
        setSelectedImage(null);
        setImagePreview(null);
    }, []);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const newData = {
                ...prev,
                [name]: value
            };
            // Générer automatiquement le slug à partir du nom
            if (name === 'name') {
                newData.slug = value
                    .toLowerCase()
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/[^a-z0-9]+/g, '-')
                    .replace(/(^-|-$)+/g, '');
            }
            return newData;
        });
    }, []);

    const handleImageChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];

            // Vérifier le type de fichier
            if (!file.type.startsWith('image/')) {
                alert('Veuillez sélectionner une image valide');
                return;
            }

            setSelectedImage(file);
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);
        }
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            let categoryId;
            let categoryData;

            if (editingCategory) {
                categoryData = await updateCategory(editingCategory._id, formData);
                categoryId = editingCategory._id;
            } else {
                categoryData = await createCategory(formData);
                categoryId = categoryData._id;
            }

            if (selectedImage) {
                try {
                    await uploadCategoryImage(categoryId, selectedImage);
                } catch (error) {
                    console.error('Erreur lors de l\'upload de l\'image:', error);
                }
            }

            handleCloseDialog();
            loadCategories();
        } catch (error) {
            console.error('Erreur lors de la sauvegarde de la catégorie:', error);
        } finally {
            setLoading(false);
        }
    }, [editingCategory, formData, selectedImage, handleCloseDialog, loadCategories]);

    const handleDelete = useCallback(async (id: string) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette catégorie ?')) {
            try {
                await deleteCategory(id);
                loadCategories();
            } catch (error) {
                console.error('Erreur lors de la suppression de la catégorie:', error);
            }
        }
    }, [loadCategories]);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Gestion des Catégories</Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Ajouter une catégorie
                </Button>
            </Box>

            <Grid container spacing={3}>
                {categories.map((category) => (
                    <Grid item xs={12} sm={6} md={4} key={category._id}>
                        <Card>
                            {category.imageUrl && (
                                <CardMedia
                                    component="img"
                                    height="140"
                                    image={category.imageUrl}
                                    alt={category.name}
                                    sx={{
                                        objectFit: 'cover',
                                        width: '100%'
                                    }}
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                    }}
                                />
                            )}
                            <CardContent>
                                <Typography variant="h6">{category.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {category.description}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Slug: {category.slug}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Produits: {category.products?.length || 0}
                                </Typography>
                            </CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
                                <IconButton onClick={() => handleOpenDialog(category)}>
                                    <EditIcon />
                                </IconButton>
                                <IconButton onClick={() => handleDelete(category._id)}>
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Nom"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            fullWidth
                        />
                        <TextField
                            label="Description"
                            name="description"
                            value={formData.description}
                            onChange={handleInputChange}
                            fullWidth
                            multiline
                            rows={4}
                        />
                        <TextField
                            label="Slug"
                            name="slug"
                            value={formData.slug}
                            onChange={handleInputChange}
                            fullWidth
                        />
                        <Box sx={{ mt: 2 }}>
                            <input
                                accept="image/*"
                                type="file"
                                id="image-upload"
                                style={{ display: 'none' }}
                                onChange={handleImageChange}
                            />
                            <label htmlFor="image-upload">
                                <Button variant="contained" component="span">
                                    Sélectionner une image
                                </Button>
                            </label>
                            {imagePreview && (
                                <Box sx={{ mt: 2, textAlign: 'center' }}>
                                    <img
                                        src={imagePreview}
                                        alt="Aperçu"
                                        style={{ maxWidth: '100%', maxHeight: '200px' }}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Annuler</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        disabled={loading}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminCategories;